import React from 'react';

import { Menu } from 'antd';
import dayjs from 'dayjs';
import styled from 'styled-components';

import Button from 'components/lib/Button';
import DropdownButton from 'components/lib/DropdownButton';
import { isMysteryShopAdmin } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import { SNAPSHOT_SELECTOR_SELECTION } from './constants';
import { SnapshotEditorContext } from './SnapshotEditorContext';
import { SNAPSHOT_MODAL_STEP } from '../constants';
import { useSnapshotContext } from '../SnapshotContext';

const StyledDropdownMenu = styled.div`
  .ant-dropdown-menu {
    padding: 0;
    border-radius: 0;
    margin-top: -3px;
  }

  .ant-dropdown-menu-item {
    border: none;
    border-top: 1px solid var(--line-gray);
    border-radius: 0 !important;

    &:first-child {
      border-top: none;
    }
  }
`;

const SnapshotEditorDropdown = () => {
  const { currentUser } = useAppSelector((state) => state.auth);

  const { snapshot, currentSnapshot } = useSnapshotContext();
  const { setStep, setSelection, setSelectedSnapshot, setDate } = React.useContext(SnapshotEditorContext);

  const handleMenuClick = ({ key }: { key: string }) => {
    const actions: Record<string, () => void> = {
      addNew: () => {
        setSelection(SNAPSHOT_SELECTOR_SELECTION.ENTER_NEW_DATA);
        setSelectedSnapshot(undefined);
        setDate(dayjs());
        setStep(SNAPSHOT_MODAL_STEP.DATA);
      },
      addHistorical: () => {
        setStep(SNAPSHOT_MODAL_STEP.EDITOR);
      },
    };

    actions[key]?.();
  };

  const onEdit = () => {
    let selection;
    let selected = undefined;
    let selectedDate = dayjs();

    const isAdmin = currentUser?.isSuperuser || isMysteryShopAdmin(currentUser);

    if (snapshot) {
      if (snapshot.latest && dayjs().isSame(snapshot.date, 'quarter')) {
        selection = SNAPSHOT_SELECTOR_SELECTION.MODIFY_CURRENT_DATA;
        selected = currentSnapshot;
        selectedDate = dayjs(currentSnapshot?.date);
      } else if (snapshot.latest && !isAdmin) {
        selection = SNAPSHOT_SELECTOR_SELECTION.ENTER_NEW_DATA;
      } else {
        selection = snapshot.draft
          ? SNAPSHOT_SELECTOR_SELECTION.ENTER_SHOP_DATA
          : SNAPSHOT_SELECTOR_SELECTION.MODIFY_HISTORICAL_DATA;
        selected = snapshot;
        selectedDate = dayjs(snapshot.date);
      }
    } else {
      selection = SNAPSHOT_SELECTOR_SELECTION.ENTER_NEW_DATA;
    }

    setSelection(selection);
    setSelectedSnapshot(selected);
    setDate(selectedDate);
    setStep(SNAPSHOT_MODAL_STEP.DATA);
  };

  const todaySnapshotExists = currentSnapshot && dayjs(currentSnapshot.date).isSame(dayjs(), 'day');

  const menu = (
    <StyledDropdownMenu>
      <Menu
        onClick={handleMenuClick}
        items={[
          { key: 'addNew', label: 'Add New Data', disabled: todaySnapshotExists },
          { key: 'addHistorical', label: 'Add Historical Data' },
        ]}
      />
    </StyledDropdownMenu>
  );

  return (
    <>
      {currentUser?.isSuperuser ? (
        <Button
          data-test-id="cd-edit-snapshot-btn"
          style={{ display: 'flex', alignItems: 'center', height: 40, width: 88, color: 'black' }}
          disabled={!snapshot}
          onClick={onEdit}
        >
          <img src="/icons/edit-note-black.svg" width={24} height={24} />
          <span>Edit</span>
        </Button>
      ) : (
        <DropdownButton onClick={onEdit} overlay={menu} dataTestId="cd-edit-snapshot-btn">
          <img src="/icons/edit-note-black.svg" width={24} height={24} style={{ marginRight: 3 }} />
          Edit
        </DropdownButton>
      )}
    </>
  );
};

export default SnapshotEditorDropdown;
